import Component from 'flarum/common/Component';
import Link from 'flarum/common/components/Link';
import Tooltip from 'flarum/common/components/Tooltip';
import humanTime from 'flarum/common/helpers/humanTime';
import icon from 'flarum/common/helpers/icon';
import Discussion from 'flarum/common/models/Discussion';
import ItemList from 'flarum/common/utils/ItemList';
import classList from 'flarum/common/utils/classList';
import app from 'flarum/forum/app';
import type Mithril from 'mithril';

interface Attrs {
  article: Discussion;
  defaultImage: string;
}

export default class FeaturedBlogItem extends Component<Attrs> {
  topItems(): ItemList<Mithril.Children> {
    const { article } = this.attrs;

    const items = new ItemList<Mithril.Children>();

    items.add(
      'tags',
      <span class="BlogFeatured-list-item-tags">
        {article.tags()?.map((tag) => (
          <span class="dataItem">{tag.name()}</span>
        ))}
      </span>,
      100
    );

    // Sticky is an optional dependency, so we can't
    // assume method existence.
    if (article.isSticky?.()) {
      items.add('sticky', <span class="BlogFeatured-list-item-isSticky dataItem">{icon('fas fa-thumbtack')}</span>, 80);
    }

    if (article.blogMeta()?.isPendingReview?.() || article.isHidden()) {
      items.add('hidden', <span class="BlogFeatured-list-item-isHidden dataItem">{icon('fas fa-eye-slash')}</span>, 60);
    }

    if (article.blogMeta()?.isPendingReview?.()) {
      items.add(
        'pendingReview',
        <Tooltip text={app.translator.trans('DeepIntegrations-flarum-blog.forum.review_article.pending_review')} position="bottom">
          <span class="BlogFeatured-list-item-pendingReview dataItem">
            {icon('far fa-clock')} {app.translator.trans('DeepIntegrations-flarum-blog.forum.review_article.pending_review_title')}
          </span>
        </Tooltip>,
        40
      );
    }

    return items;
  }

  dataItems(): ItemList<Mithril.Children> {
    const { article } = this.attrs;

    const items = new ItemList<Mithril.Children>();

    items.add(
      'createdAt',
      <span class="BlogFeatured-list-item-details-createdAt">
        {icon('far fa-clock')} {humanTime(article.createdAt())}
      </span>,
      100
    );

    items.add(
      'author',
      <span class="BlogFeatured-list-item-details-author">
        {icon('far fa-user')} {article.user()?.displayName() || app.translator.trans('core.lib.username.deleted_text')}
      </span>,
      80
    );

    items.add(
      'replies',
      <span class="BlogFeatured-list-item-details-replies">
        {icon('far fa-comment')} {article.commentCount() - 1}
      </span>,
      60
    );

    return items;
  }

  view(vnode: Mithril.Vnode<Attrs, this>) {
    const { article, defaultImage } = this.attrs;

    const blogImage = article.blogMeta()?.featuredImage?.() ? `url(${article.blogMeta().featuredImage()})` : defaultImage;
    const isAdmin = app.session.user?.isAdmin?.() === true;
    const isLocked = !isAdmin && article.isCourseModuleLocked?.() === true;

    // Time-gated unlocks (previous module completed, waiting on 8 AM ET
    // rollover) carry a courseModuleAvailableAt; show the date so the
    // user knows when this module opens instead of the generic prompt.
    let lockOverlayText = 'Complete the previous module to unlock this one.';
    if (isLocked && typeof (article as any).courseModuleAvailableAt === 'function') {
      const raw = (article as any).courseModuleAvailableAt();
      const when = raw instanceof Date ? raw : raw ? new Date(raw) : null;
      if (when && !isNaN(when.getTime()) && when.getTime() > Date.now()) {
        const day = when.toLocaleDateString('en-US', {
          timeZone: 'America/New_York',
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        });
        lockOverlayText = `Available ${day} at 8:00 AM Eastern Time.`;
      }
    }

    const cardClassName = classList(
      'BlogFeatured-list-item',
      article.tags().map((tag) => `BlogFeatured-list-item-category-${tag.id()}`),
      {
        'BlogFeatured-list-item-locked': isLocked,
      },
      'FlarumBlog-default-image'
    );
    const content = [
      <div class="BlogFeatured-list-item-top">{this.topItems().toArray()}</div>,

      <div className={'BlogFeatured-list-item-details'}>
        {isLocked && (
          <div className="BlogFeatured-list-item-lockOverlay">
            <div className="BlogFeatured-list-item-lockOverlayBadge">{icon('fas fa-lock')} Locked</div>
            <div className="BlogFeatured-list-item-lockOverlayText">{lockOverlayText}</div>
          </div>
        )}
        <h4>{article.title()}</h4>

        <div className={'data'}>{this.dataItems().toArray()}</div>
      </div>,
    ];

    if (isLocked) {
      return (
        <div className={cardClassName} style={{ backgroundImage: blogImage }}>
          {content}
        </div>
      );
    }

    return (
      <Link
        href={app.route('blogArticle', {
          id: `${article.slug()}`,
        })}
        className={cardClassName}
        style={{ backgroundImage: blogImage }}
      >
        {content}
      </Link>
    );
  }
}
